import React, {
  useState,
  useEffect,
  useCallback
} from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import CmsFsPagesSectionsApiV2 from 'api/v2/admins/cms/featured-sellers/home-page-sections-api-v2'
import CmsFeaturedSellerLandingPageFsTable from 'domains/cms-featured-seller/organisms/landing-page-fs-table'
import CmsFeaturedSellerLandingPageRelatedArticleTable from 'domains/cms-featured-seller/organisms/landing-page-related-article-table'
import CmsFeaturedSellerSearchForm from 'domains/cms-featured-seller/organisms/search-form'
import CmsBlogPostSearchForm from 'domains/cms-blog-post/organisms/search-form'
import StringUtils from 'utils/string-util'
import PropTypes from 'prop-types'
import { ALERT_TYPES } from 'constants/alert-constants'
import { SECTION } from 'domains/cms-featured-seller/constants'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const CmsFeaturedSellerLandingPage = ({ pageUtils }) => {
  const [ sectionId, setSectionId ]             = useState({
    highlight      : 0,
    recentlyLive   : 0,
    relatedArticle : 0
  })
  const [ featuredSellers, setFeaturedSellers ] = useState({
    highlight       : [],
    recentlyLive    : [],
    relatedArticles : []
  })
  const [ activeTab, setActiveTab ]             = useState(SECTION.HIGHLIGHT)

  // Fetch featured seller section data
  const handleFetchFeaturedSellerSection = useCallback(() => {
    CmsFsPagesSectionsApiV2.get()
      .then((response) => {
        response.data.home_page_sections.forEach((section) => {
          switch (section.name) {
            case 'Highlights':
              setFeaturedSellers((prevState) => ({ ...prevState , highlight: section.closets }))
              setSectionId((prevState) => ({ ...prevState , highlight: section.id }))
              break
            case 'Recently Live':
              setFeaturedSellers((prevState) => ({ ...prevState , recentlyLive: section.closets }))
              setSectionId((prevState) => ({ ...prevState , recentlyLive: section.id }))
              break
            case 'Related Articles':
              setFeaturedSellers((prevState) => ({ ...prevState , relatedArticles: section.blog_posts }))
              setSectionId((prevState) => ({ ...prevState , relatedArticles: section.id }))
              break
          }
        })
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleFetchFeaturedSellerSection()
  }, [ handleFetchFeaturedSellerSection, pageUtils ])

  // Add featured seller to highlight section
  const handleClickAddHighlightFeaturedSeller = async (values, formikBag) => {
    const { closet_id } = values

    await CmsFsPagesSectionsApiV2.addCloset(sectionId.highlight, closet_id)
      .then((response) => {
        setFeaturedSellers((prevState) => ({
          ...prevState,
          highlight: response.data.home_page_section.closets
        }))
        formikBag.resetForm()
        pageUtils.setAlertMsg('Successfully added Closet to Recently Live section!', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  // Add featured seller to recently live section
  const handleClickAddRecentlyLiveFeaturedSeller = async (values) => {
    const { closet_id } = values

    await CmsFsPagesSectionsApiV2.addCloset(sectionId.recentlyLive, closet_id)
      .then((response) => {
        setFeaturedSellers((prevState) => ({
          ...prevState,
          recentlyLive: response.data.home_page_section.closets
        }))
        pageUtils.setAlertMsg('Successfully remove featured seller from recently live section!', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  // Add blog post to related article section
  const handleClickAddRelatedArticle = async (values) => {
    const { blog_post_id } = values

    await CmsFsPagesSectionsApiV2.addBlogPost(sectionId.relatedArticles, blog_post_id)
      .then((response) => {
        setFeaturedSellers((prevState) => ({
          ...prevState,
          relatedArticles: response.data.home_page_section.blog_posts
        }))

        pageUtils.setAlertMsg('Successfully add blog post to related article section!', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  // Remove featured seller from highlight section
  const handleClickRemoveHighlightFeaturedSeller = async (seller) => {
    const isRemoveAction = window.confirm('Are you sure to remove this featured seller?')

    if (isRemoveAction) {
      const newHighlightFeaturedSellers = featuredSellers.highlight.filter((fs) => seller.id !== fs.id )
      await CmsFsPagesSectionsApiV2.removeCloset(sectionId.highlight, seller.id)
        .then(() => {
          setFeaturedSellers((prevState) => ({
            ...prevState,
            highlight: newHighlightFeaturedSellers
          }))
          pageUtils.setAlertMsg('Successfully remove featured seller from highlight section!', ALERT_TYPES.SUCCESS)
        })
        .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
    }
  }

  // Remove featured seller from recently live section
  const handleClickRemoveRecentlyLiveFeaturedSeller = async (seller) => {
    const isRemoveAction = window.confirm('Are you sure to remove this featured seller?')

    if (isRemoveAction) {
      const newRecentlyLiveFeaturedSellers = featuredSellers.recentlyLive.filter((fs) => seller.id !== fs.id )

      await CmsFsPagesSectionsApiV2.removeCloset(sectionId.recentlyLive, seller.id)
        .then(() => {
          setFeaturedSellers((prevState) => ({
            ...prevState,
            recentlyLive: newRecentlyLiveFeaturedSellers
          }))
          pageUtils.setAlertMsg('Successfully remove featured seller from recently live section!', ALERT_TYPES.SUCCESS)
        })
        .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
    }
  }

  // Remove blog post from related article section
  const handleClickRemoveRelatedArticle = async (relatedArticle) => {
    const isRemoveAction = window.confirm('Are you sure to remove this blog post?')

    if (isRemoveAction) {
      await CmsFsPagesSectionsApiV2.removeBlogPost(sectionId.relatedArticles, relatedArticle.id)
        .then((response) => {
          setFeaturedSellers((prevState) => ({
            ...prevState,
            relatedArticles: response.data.home_page_section.blog_posts
          }))
          pageUtils.setAlertMsg('Successfully remove blog post from related article section!', ALERT_TYPES.SUCCESS)
        })
        .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
    }
  }

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  return (
    <>
      <TitlePage mainTitle={ 'CMS Featured Seller' } subTitle={ 'Landing Page' } />

      <Nav pills className={ 'mb-3' }>
        <NavItem>
          <NavLink
            href="#"
            className={ activeTab === SECTION.HIGHLIGHT ? 'active' : null }
            onClick={ () => toggleTab(SECTION.HIGHLIGHT) }
          >
            { StringUtils.titleCase(SECTION.HIGHLIGHT) }
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            href="#"
            className={ activeTab === SECTION.RECENTLY_LIVE ? 'active' : null }
            onClick={ () => toggleTab(SECTION.RECENTLY_LIVE) }
          >
            { StringUtils.titleCase(SECTION.RECENTLY_LIVE) }
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            href="#"
            className={ activeTab === SECTION.RELATED_ARTICLE ? 'active' : null }
            onClick={ () => toggleTab(SECTION.RELATED_ARTICLE) }
          >
            { StringUtils.titleCase(SECTION.RELATED_ARTICLE) }
          </NavLink>
        </NavItem>
      </Nav>

      { /* Highlight Section */ }
      <TabContent activeTab={ activeTab }>
        <TabPane tabId={ SECTION.HIGHLIGHT }>
          <Card>
            <CardHeader>
              <h2>
                { 'Highlight Section' }
              </h2>
            </CardHeader>
            <CardBody>
              <CmsFeaturedSellerSearchForm
                handleSubmitForm={ handleClickAddHighlightFeaturedSeller }
              />
            </CardBody>
            <CmsFeaturedSellerLandingPageFsTable
              data={ featuredSellers?.highlight }
              hiddenColumns={ [] }
              rowButtonProps={{
                buttonText    : 'Remove',
                buttonColour  : 'danger',
                onButtonClick : handleClickRemoveHighlightFeaturedSeller
              }}
            />
          </Card>
        </TabPane>

        { /* Recently Live Section */ }
        <TabPane tabId={ SECTION.RECENTLY_LIVE }>
          <Card>
            <CardHeader>
              <h2>
                { 'Recently Live Section' }
              </h2>
            </CardHeader>
            <CardBody>
              <CmsFeaturedSellerSearchForm
                handleSubmitForm={ handleClickAddRecentlyLiveFeaturedSeller }
              />
            </CardBody>
            <CmsFeaturedSellerLandingPageFsTable
              data={ featuredSellers?.recentlyLive }
              hiddenColumns={ [] }
              rowButtonProps={{
                buttonText    : 'Remove',
                buttonColour  : 'danger',
                onButtonClick : handleClickRemoveRecentlyLiveFeaturedSeller
              }}
            />
          </Card>
        </TabPane>

        { /* Related Article Section */ }
        <TabPane tabId={ SECTION.RELATED_ARTICLE }>
          <Card>
            <CardHeader>
              <h2>
                { 'Related Article Section' }
              </h2>
            </CardHeader>
            <CardBody>
              <CmsBlogPostSearchForm
                handleSubmitForm={ handleClickAddRelatedArticle }
              />
            </CardBody>
            <CmsFeaturedSellerLandingPageRelatedArticleTable
              data={ featuredSellers?.relatedArticles }
              hiddenColumns={ [] }
              rowButtonProps={{
                buttonText    : 'Remove',
                buttonColour  : 'danger',
                onButtonClick : handleClickRemoveRelatedArticle
              }}
            />
          </Card>
        </TabPane>
      </TabContent>
    </>
  )
}

CmsFeaturedSellerLandingPage.propTypes    = propTypes
CmsFeaturedSellerLandingPage.defaultProps = defaultProps

export default CmsFeaturedSellerLandingPage
